import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { isSameDay as isSameDayFns, startOfMonth } from 'date-fns';
import { AvailableShift } from 'components/FinancialCheckup/FinancialDashboard/AvailableShiftsSection/useAvailableShifts';
import { startOfDay } from 'utils/dateUtils';

import {
  filterShiftsForCalendar,
  getAvailableDateKeysFromShifts,
  getInitialVisibleEndDate,
  getNextVisibleEndDate,
  getViewStartDate,
  hasMoreShiftsInMonth,
} from './shiftCalendarUtils';

export interface UseShiftCalendarLogicResult {
  selectedDate: Date | undefined;
  selectedMonth: Date;
  filteredShifts: AvailableShift[];
  availableDateKeys: string[];
  hasRemainingShifts: boolean;
  handleDateSelect: (date: Date) => void;
  handleMonthChange: (month: Date) => void;
  handleShowMore: () => void;
  handleSeeAll: () => void;
}

const computeDefaultVisibleEndDate = (shifts: AvailableShift[], month: Date, viewStart: Date, today: Date): Date =>
  getInitialVisibleEndDate(shifts, month, viewStart, today);

export const useShiftCalendarLogic = (shifts: AvailableShift[]): UseShiftCalendarLogicResult => {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedMonth, setSelectedMonth] = useState(() => startOfMonth(today));
  const [hasUserExpandedWeeks, setHasUserExpandedWeeks] = useState(false);

  const viewStartDate = useMemo(() => getViewStartDate(selectedMonth, today), [selectedMonth, today]);

  const [visibleEndDate, setVisibleEndDate] = useState(() =>
    computeDefaultVisibleEndDate(shifts, startOfMonth(today), getViewStartDate(startOfMonth(today), today), today),
  );

  const shiftsRef = useRef(shifts);
  shiftsRef.current = shifts;

  useEffect(() => {
    if (selectedDate || hasUserExpandedWeeks) return;

    const nextVisibleEnd = computeDefaultVisibleEndDate(shiftsRef.current, selectedMonth, viewStartDate, today);

    setVisibleEndDate((current) => (current.getTime() === nextVisibleEnd.getTime() ? current : nextVisibleEnd));
  }, [shifts, selectedMonth, selectedDate, hasUserExpandedWeeks, viewStartDate, today]);

  const filteredShifts = useMemo(
    () =>
      filterShiftsForCalendar({
        shifts,
        selectedMonth,
        selectedDate,
        viewStartDate,
        visibleEndDate,
        today,
      }),
    [shifts, selectedMonth, selectedDate, viewStartDate, visibleEndDate, today],
  );

  const availableDateKeys = useMemo(() => getAvailableDateKeysFromShifts(shifts, viewStartDate), [
    shifts,
    viewStartDate,
  ]);

  const hasRemainingShifts = useMemo(() => {
    if (selectedDate) return false;

    return hasMoreShiftsInMonth(shifts, selectedMonth, viewStartDate, visibleEndDate);
  }, [shifts, selectedMonth, viewStartDate, visibleEndDate, selectedDate]);

  const resetVisibleRange = useCallback(() => {
    const nextVisibleEnd = computeDefaultVisibleEndDate(shifts, selectedMonth, viewStartDate, today);
    setVisibleEndDate((current) => (current.getTime() === nextVisibleEnd.getTime() ? current : nextVisibleEnd));
  }, [shifts, selectedMonth, viewStartDate, today]);

  const handleShowMore = useCallback(() => {
    setHasUserExpandedWeeks(true);
    setVisibleEndDate((current) => getNextVisibleEndDate(current, selectedMonth));
  }, [selectedMonth]);

  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate((current) => {
        if (current && isSameDayFns(current, date)) {
          setHasUserExpandedWeeks(false);
          const nextVisibleEnd = computeDefaultVisibleEndDate(shifts, selectedMonth, viewStartDate, today);
          setVisibleEndDate((prev) => (prev.getTime() === nextVisibleEnd.getTime() ? prev : nextVisibleEnd));
          return undefined;
        }

        return date;
      });
    },
    [shifts, selectedMonth, viewStartDate, today],
  );

  const handleSeeAll = useCallback(() => {
    setSelectedDate(undefined);
    setHasUserExpandedWeeks(false);
    resetVisibleRange();
  }, [resetVisibleRange]);

  const handleMonthChange = useCallback(
    (month: Date) => {
      const monthStart = startOfMonth(month);
      const nextViewStart = getViewStartDate(monthStart, today);

      setSelectedMonth(monthStart);
      setSelectedDate(undefined);
      setHasUserExpandedWeeks(false);
      setVisibleEndDate(computeDefaultVisibleEndDate(shifts, monthStart, nextViewStart, today));
    },
    [shifts, today],
  );

  return {
    selectedDate,
    selectedMonth,
    filteredShifts,
    availableDateKeys,
    hasRemainingShifts,
    handleDateSelect,
    handleMonthChange,
    handleShowMore,
    handleSeeAll,
  };
};
